import React, { useState, useRef } from 'react';
import { Button, Avatar, Progress, Chip } from '@heroui/react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:8080';

const AvatarUpload = ({ currentPhotoKey, currentPhotoURL, onUploadComplete }) => {
  const { getIdToken } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewURL, setPreviewURL] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('Image must be less than 5MB');
      return;
    }

    setError('');
    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Create preview
      const reader = new FileReader();
      reader.onload = (e) => setPreviewURL(e.target.result);
      reader.readAsDataURL(file);

      // Step 2: Get presigned upload URL from backend
      const idToken = await getIdToken();
      const uploadResponse = await fetch(`${API_BASE_URL}/api/auth/upload-avatar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          contentType: file.type,
          fileSize: file.size,
        }),
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.message || 'Failed to get upload URL');
      }

      const { data } = await uploadResponse.json();
      const { uploadUrl, photoKey } = data;

      setUploadProgress(30);

      // Step 3: Upload directly to R2
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = 30 + Math.round((e.loaded / e.total) * 70);
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          setUploadProgress(100);
          setUploading(false);

          // Notify parent component
          if (onUploadComplete) {
            onUploadComplete(photoKey);
          }
        } else {
          setError('Upload failed. Please try again.');
          setUploading(false);
          setPreviewURL(null);
        }
      });

      xhr.addEventListener('error', () => {
        setError('Upload failed. Please check your connection.');
        setUploading(false);
        setPreviewURL(null);
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);

    } catch (err) {
      setError(err.message || 'Upload failed');
      setUploading(false);
      setPreviewURL(null);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const displayURL = previewURL || currentPhotoURL || currentPhotoKey;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-6">
        {/* Avatar Preview */}
        <Avatar
          src={displayURL}
          alt="Profile picture"
          className="w-24 h-24 text-large"
          isBordered
        />

        {/* Upload Button and Status */}
        <div className="flex-1 space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            onChange={handleFileSelect}
            className="hidden"
          />

          <Button
            color="primary"
            variant="flat"
            onPress={handleButtonClick}
            isDisabled={uploading}
            size="md"
          >
            {uploading ? 'Uploading...' : 'Choose Image'}
          </Button>

          {uploading && (
            <div className="space-y-2">
              <Progress
                value={uploadProgress}
                color="primary"
                size="sm"
                className="max-w-md"
              />
              <p className="text-xs text-gray-500">{uploadProgress}% uploaded</p>
            </div>
          )}

          {error && (
            <Chip color="danger" variant="flat" size="sm">
              {error}
            </Chip>
          )}

          <p className="text-xs text-gray-500">
            Supported: JPEG, PNG, WebP, GIF • Max size: 5MB
          </p>
        </div>
      </div>
    </div>
  );
};

export default AvatarUpload;
